import { ChangeEvent, forwardRef, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { useOnClickOutside, useResizeObserver } from 'usehooks-ts';
import classNames from 'classnames';

import { useScreenSize } from '@/lib/hooks/use-screen-size';
import { Overlay } from '@/components/overlay';
import { Button } from '@/components/button';

import {
  ensureHexPrefix,
  hasNoColor,
  isValidHexColor,
} from '@/explore/utils/conditional-formatting';

import { FormInputSize } from '../types';
import { Input } from '../input';

import styles from './color-input.module.scss';

interface ColorPickerProps {
  color: string;
  presetColors?: string[];
  allowNone?: boolean;
  size?: FormInputSize;
  label?: string;
  trigger?: ReactNode;
  onChange: (color: string | undefined) => void;
  className?: string;
}

const PositionPadding = 40;

type Position = {
  top: number;
  left: number;
};

const Popover = (props: {
  color: string;
  presetColors?: string[];
  allowNone?: boolean;
  position: Position;
  onChange: (color: string | undefined) => void;
  onClose: () => void;
}) => {
  const { allowNone = false, onChange, onClose } = props;
  const popover = useRef<HTMLDivElement>(null!);

  const screenSize = useScreenSize();
  useOnClickOutside(popover, onClose);
  const popoverSize = useResizeObserver({
    ref: popover,
    box: 'content-box',
  });
  const [color, setColor] = useState(() =>
    hasNoColor(props.color) ? '' : ensureHexPrefix(props.color),
  );
  const noColor = hasNoColor(color);

  const position = useMemo(() => {
    if (popoverSize.width === undefined || popoverSize.height === undefined) {
      return { top: 0, left: 0, opacity: 0 };
    }
    const left =
      props.position.left + popoverSize.width + PositionPadding > screenSize.width
        ? screenSize.width - popoverSize.width - PositionPadding
        : props.position.left;
    const top =
      props.position.top + popoverSize.height + PositionPadding > screenSize.height
        ? screenSize.height - popoverSize.height - PositionPadding
        : props.position.top;
    return { top, left };
  }, [popoverSize.width, popoverSize.height, props.position, screenSize.width, screenSize.height]);

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      if (newValue === '#') {
        setColor('');
      } else {
        setColor(ensureHexPrefix(newValue));
      }
    },
    [setColor],
  );

  const handleDone = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent) => {
      e.stopPropagation();
      if (allowNone && noColor) {
        onChange(undefined);
        onClose();
        return;
      }
      if (isValidHexColor(color)) {
        onChange(color);
        onClose();
        return;
      }
    },
    [allowNone, color, noColor, onChange, onClose],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleDone(e);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  };

  const handleColorChange = useCallback(
    (newColor: string) => {
      if (isValidHexColor(newColor)) {
        setColor(newColor);
      }
    },
    [setColor],
  );

  return (
    <div
      ref={popover}
      className={classNames(styles.colorPopover, {
        [styles.noColorPopover]: props.allowNone === true && noColor,
      })}
      onKeyDown={handleKeyDown}
      style={position}>
      <HexColorPicker color={color} onChange={handleColorChange} />
      {props.presetColors !== undefined && props.presetColors.length > 0 && (
        <div className={styles.colorSwatches}>
          {props.presetColors.map((presetColor) => (
            <button
              type="button"
              key={presetColor}
              aria-label={`Select color ${presetColor}`}
              className={classNames(styles.colorSwatch, {
                [styles.selected]: color === presetColor,
              })}
              style={{ background: presetColor }}
              onClick={() => setColor(presetColor)}
            />
          ))}
        </div>
      )}
      <div className={styles.colorInput}>
        <Input
          type="text"
          value={color}
          placeholder={noColor ? 'No color' : undefined}
          autoFocus
          onChange={handleInputChange}
        />
      </div>
      <div className={styles.buttons}>
        <Button size="small" onClick={handleDone}>
          Done
        </Button>
        {props.allowNone === true && (
          <button
            type="button"
            aria-label="No color"
            className={classNames(styles.colorSwatch, styles.noColorSwatch, {
              [styles.selected]: noColor,
            })}
            onClick={(e) => {
              e.stopPropagation();
              setColor('');
            }}
          />
        )}
      </div>
    </div>
  );
};

export const ColorPicker = forwardRef<HTMLDivElement, ColorPickerProps>(
  function ColorPicker(props, ref) {
    const {
      color,
      presetColors,
      allowNone = false,
      size = 'medium',
      label,
      trigger,
      onChange,
      className,
    } = props;
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
    const colorLabelRef = useRef<HTMLDivElement>(null);

    const handleClick = () => {
      if (colorLabelRef.current) {
        const rect = colorLabelRef.current.getBoundingClientRect();
        setPosition({ top: rect.bottom + 8, left: rect.left });
      }
    };

    const isNone = allowNone && hasNoColor(color);

    return (
      <div ref={ref} className={classNames(styles.colorPicker, className)} onClick={handleClick}>
        {trigger !== undefined ? (
          <div ref={colorLabelRef}>{trigger}</div>
        ) : (
          <div className={styles.colorLabel} ref={colorLabelRef}>
            <div
              className={classNames(styles.colorToggle, {
                [styles.toggleSmall]: size === 'small',
                [styles.toggleLarge]: size === 'large',
                [styles.noColorToggle]: isNone,
              })}
              style={isNone ? undefined : { backgroundColor: color }}
            />
            {label}
          </div>
        )}
        {position !== null && (
          <Overlay>
            <Popover
              color={color}
              presetColors={presetColors}
              allowNone={allowNone}
              position={position}
              onChange={onChange}
              onClose={() => setPosition(null)}
            />
          </Overlay>
        )}
      </div>
    );
  },
);
